/* eslint-disable indent */
import React from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { Loader } from 'src/components/Loader';

interface ButtonProps {
  text: any;
  disabled?: boolean;
  isLoading?: boolean;
  className?: string;
  $fullWidth?: boolean;
  $isDark?: boolean;
  styles?: any;
  // eslint-disable-next-line no-unused-vars
  onClick?: (e: any) => void;
}

const ButtonStyles = css<Omit<ButtonProps, 'text'>>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.6rem 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
  border-radius: 0.5rem;
  align-self: baseline;
  font-weight: 700;
  font-size: 1rem;
  /* Fixed so the box is the same height whether it holds the label or the
     spinner that replaces it. */
  line-height: 1.2;

  /* The spinner stands in for the label, so it is sized from the label's own
     font rather than a fixed pixel box — a 24px one outgrew the text line and
     stretched the button, and the compact variants worse than the rest. */
  & > div[role='status'] {
    width: 1.2em;
    height: 1.2em;
    border-width: 2px;
  }

  background-color: ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.alwaysLight};
  border: none;

  // Set disabled button text color to theme.grey
  ${({ disabled, theme }) =>
    disabled &&
    `
      pointer-events: none; 
      background-color: ${theme.grey}; 
      opacity: 0.5;
      cursor: not-allowed;
    `}

  // Universal hover effect for all variants
  &:hover:not(:disabled) {
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
    filter: brightness(1.1);
  }

  &:active:not(:disabled) {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    filter: brightness(0.95);
  }

  &.positive {
    background: ${({ theme }) => theme.positive};
  }

  &.negative {
    background: ${({ theme }) => theme.negative};
  }

  &.light {
    background: ${({ theme }) => theme.light};
    color: ${({ theme }) => theme.dark};
  }

  &.lightGrey {
    background: ${({ theme }) => theme.lightGrey};
    color: ${({ theme }) => theme.dark};
  }

  &.plainText {
    background: transparent;
    color: ${({ theme }) => theme.dark};

    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.lightGrey};
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
  }

  &.outline {
    color: ${({ theme }) => theme.dark};
    background-color: transparent;
    box-shadow: 0 0 0 1px ${({ theme }) => theme.midGrey};
    padding: 0.5125rem 1.5rem;

    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.lightGrey};
      box-shadow: 0 0 0 1px ${({ theme }) => theme.grey}, 0 4px 12px rgba(0, 0, 0, 0.1);
    }
  }

  &.black {
    background-color: ${({ theme }) => theme.dark};
    color: ${({ theme }) => theme.light};
  }

  &.small {
    padding: 0.25rem 0.75rem;
    font-size: 0.75rem;

    &:hover:not(:disabled) {
      box-shadow: 0 3px 10px rgba(0, 0, 0, 0.12);
    }
  }
`;

const StyledButton = styled.button<Omit<ButtonProps, 'text'>>`
  ${ButtonStyles}
`;

export const Button: React.FC<ButtonProps> = ({
  text,
  disabled,
  isLoading,
  className,
  $fullWidth,
  styles,
  onClick,
  ...rest
}) => {
  const { isDark } = useSelector(({ isDark }) => isDark);

  return (
    <StyledButton
      onClick={onClick}
      disabled={disabled}
      className={className}
      $fullWidth={$fullWidth}
      $isDark={isDark}
      style={styles}
      {...rest}
    >
      {isLoading ? <Loader size={16} /> : text}
    </StyledButton>
  );
};
